/**
 * PIPE: the web staff app has exactly ONE OIDC start — the SERVER-side `GET /api/oauth/login`
 * (core-api mints PKCE/state, 302s to Keycloak, and returns through the server callback
 * `/api/oauth/cb`, which 302s straight into the app). Web code must NEVER start OIDC through the
 * client SPA pair `/oauth/login` -> `/oauth/callback`: that pair's redirect_uri is the SPA
 * `/oauth/callback` route, so after the Keycloak login the browser loads an intermediate AFFiNE
 * auth document before the dashboard (the post-login "custom OIDC page" flash). The client pair
 * is kept ONLY for native (Electron/Capacitor), which needs the deep-link bridge.
 */

export const SERVER_OIDC_LOGIN_PATH = '/api/oauth/login';

/** The server-side OIDC start URL. Takes no params by design: the endpoint accepts no
 * caller-supplied redirect_uri (open-redirect defense) and always lands on the app root. */
export function serverOidcLoginUrl(baseUrl: string): string {
  return `${baseUrl}${SERVER_OIDC_LOGIN_PATH}`;
}

/** sessionStorage key stamping when the signed-out auto-redirect last fired (the loop-breaker). */
export const OIDC_AUTO_REDIRECT_AT_KEY = 'pipe:oidc-auto-redirect-at';

/** Refuse another automatic redirect within this window after the last one (ms). */
export const OIDC_AUTO_REDIRECT_COOLDOWN_MS = 20_000;

/**
 * Loop-breaker-gated automatic OIDC start for a signed-out SPA boot (the safety net for when the
 * Caddy cookie-presence gate misses, e.g. a present-but-expired session cookie). Returns the
 * server OIDC start URL and stamps the loop-breaker when the redirect should fire; returns `null`
 * when one already fired within the cooldown window — i.e. the OIDC round-trip bounced back here
 * signed-out (Keycloak down / callback error) — so the caller degrades to the visible /sign-in
 * page instead of redirecting forever.
 */
export function serverOidcAutoRedirectUrl(
  baseUrl: string,
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  now: number
): string | null {
  const lastAttempt = Number(storage.getItem(OIDC_AUTO_REDIRECT_AT_KEY) ?? 0);
  if (!(now - lastAttempt > OIDC_AUTO_REDIRECT_COOLDOWN_MS)) {
    return null;
  }
  storage.setItem(OIDC_AUTO_REDIRECT_AT_KEY, String(now));
  return serverOidcLoginUrl(baseUrl);
}
